import React, { useState, useRef, useEffect } from 'react';
import { RevisionArea, RevisionParameter } from '../../../types';
import { KlFullModal } from '../modal/KlFullModal';
import { KlDialog } from '../modal/KlDialog';

interface MobileRoadmapConfigModalProps {
    open: boolean;
    onClose: () => void;
    revisionAreas: RevisionArea[];
    setRevisionAreas: React.Dispatch<React.SetStateAction<RevisionArea[]>>;
}

export const MobileRoadmapConfigModal: React.FC<MobileRoadmapConfigModalProps> = ({
    open,
    onClose,
    revisionAreas,
    setRevisionAreas
}) => {
    // Acordeón: solo la primera área abierta por defecto
    const [expandedAreaIds, setExpandedAreaIds] = useState<Set<string>>(() => {
        return new Set(revisionAreas[0] ? [revisionAreas[0].id] : []);
    });

    const [activeMenuKey, setActiveMenuKey] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Diálogos de Área y Parámetro
    const [areaDialogOpen, setAreaDialogOpen] = useState(false);
    const [editingArea, setEditingArea] = useState<{ id?: string; title: string; maxDiscountPercent: number } | null>(null);

    const [paramDialogOpen, setParamDialogOpen] = useState(false);
    const [editingParam, setEditingParam] = useState<{ areaId: string; paramId?: string; name: string } | null>(null);

    // Diálogo de Confirmación de Eliminación
    const [confirmDelete, setConfirmDelete] = useState<{
        type: 'area' | 'param';
        areaId: string;
        paramId?: string;
        title: string;
    } | null>(null);

    // Cerrar menú contextual al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setActiveMenuKey(null);
            }
        };
        if (activeMenuKey) {
            document.addEventListener('pointerdown', handleClickOutside);
            return () => document.removeEventListener('pointerdown', handleClickOutside);
        }
    }, [activeMenuKey]);

    const toggleAreaExpand = (areaId: string) => {
        setExpandedAreaIds(prev => {
            const next = new Set(prev);
            if (next.has(areaId)) next.delete(areaId);
            else next.add(areaId);
            return next;
        });
    };

    // Handlers para Área
    const handleOpenCreateArea = () => {
        setEditingArea({ title: '', maxDiscountPercent: 10 });
        setAreaDialogOpen(true);
    };

    const handleOpenEditArea = (area: RevisionArea) => {
        setEditingArea({
            id: area.id,
            title: area.title,
            maxDiscountPercent: area.maxDiscountPercent !== undefined ? area.maxDiscountPercent : 10
        });
        setActiveMenuKey(null);
        setAreaDialogOpen(true);
    };

    const handleSaveArea = () => {
        if (!editingArea || !editingArea.title.trim()) return;
        const discount = Math.max(0, Math.min(100, editingArea.maxDiscountPercent || 10));

        if (editingArea.id) {
            setRevisionAreas(prev => prev.map(a =>
                a.id === editingArea.id
                    ? { ...a, title: editingArea.title.trim(), maxDiscountPercent: discount }
                    : a
            ));
        } else {
            const newId = `area-${Date.now()}`;
            const newArea: RevisionArea = {
                id: newId,
                title: editingArea.title.trim(),
                maxDiscountPercent: discount,
                parameters: []
            };
            setRevisionAreas(prev => [...prev, newArea]);
            setExpandedAreaIds(prev => new Set([...prev, newId]));
        }
        setAreaDialogOpen(false);
        setEditingArea(null);
    };

    const handleDeleteAreaConfirm = () => {
        if (!confirmDelete || confirmDelete.type !== 'area') return;
        setRevisionAreas(prev => prev.filter(a => a.id !== confirmDelete.areaId));
        setConfirmDelete(null);
    };

    // Handlers para Parámetro
    const handleOpenCreateParam = (areaId: string) => {
        setEditingParam({ areaId, name: '' });
        setActiveMenuKey(null);
        setParamDialogOpen(true);
    };

    const handleOpenEditParam = (areaId: string, param: RevisionParameter) => {
        setEditingParam({ areaId, paramId: param.id, name: param.name });
        setActiveMenuKey(null);
        setParamDialogOpen(true);
    };

    const handleSaveParam = () => {
        if (!editingParam || !editingParam.name.trim()) return;
        const name = editingParam.name.trim();

        if (editingParam.paramId) {
            setRevisionAreas(prev => prev.map(a =>
                a.id === editingParam.areaId
                    ? {
                        ...a,
                        parameters: a.parameters.map(p =>
                            p.id === editingParam.paramId ? { ...p, name } : p
                        )
                    }
                    : a
            ));
        } else {
            const newParam: RevisionParameter = {
                id: `param-${Date.now()}`,
                name
            };
            setRevisionAreas(prev => prev.map(a =>
                a.id === editingParam.areaId
                    ? { ...a, parameters: [...a.parameters, newParam] }
                    : a
            ));
        }
        setParamDialogOpen(false);
        setEditingParam(null);
    };

    const handleDeleteParamConfirm = () => {
        if (!confirmDelete || confirmDelete.type !== 'param' || !confirmDelete.paramId) return;
        setRevisionAreas(prev => prev.map(a =>
            a.id === confirmDelete.areaId
                ? { ...a, parameters: a.parameters.filter(p => p.id !== confirmDelete.paramId) }
                : a
        ));
        setConfirmDelete(null);
    };

    const totalAreas = revisionAreas.length;
    const totalParams = revisionAreas.reduce((sum, a) => sum + a.parameters.length, 0);

    return (
        <KlFullModal
            open={open}
            onClose={onClose}
            title="Configuración Hoja de Ruta"
            subtitle="Checklist de áreas y parámetros para Estados de Pago"
            footer={
                <div className="flex items-center justify-between w-full">
                    <span className="text-[12px] font-medium text-[#64748B] dark:text-slate-400">
                        {totalAreas} áreas · {totalParams} parámetros
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="b b-p px-6 h-10 rounded-[10px] text-[13px] font-bold shrink-0"
                    >
                        Listo / Guardar
                    </button>
                </div>
            }
        >
            <div className="flex flex-col gap-3 p-3 pb-8">
                {/* Botón tonal de 40px al inicio del cuerpo */}
                <button
                    type="button"
                    onClick={handleOpenCreateArea}
                    className="b b-t w-full h-10 rounded-[10px] text-[13px] font-bold flex items-center justify-center gap-1.5 shrink-0"
                >
                    <span className="material-symbols-outlined text-[18px]">add</span>
                    <span>Agregar Área de Evaluación</span>
                </button>

                {/* Lista vertical de Áreas en Acordeón */}
                {revisionAreas.length === 0 ? (
                    <div className="p-8 text-center text-xs text-slate-400 bg-white dark:bg-slate-800/50 rounded-[14px] border border-dashed border-[#E2E8F0] dark:border-slate-700">
                        No hay áreas de evaluación configuradas. Presiona el botón superior para agregar una.
                    </div>
                ) : (
                    revisionAreas.map((area, areaIdx) => {
                        const isExpanded = expandedAreaIds.has(area.id);
                        const areaMenuKey = `area-${area.id}`;
                        const isAreaMenuOpen = activeMenuKey === areaMenuKey;

                        return (
                            <div
                                key={area.id}
                                className="card bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700 overflow-visible relative"
                            >
                                {/* Primera línea: número en cuadro de 24px, nombre 14px/700, ⋮ y chevron */}
                                <div className="flex items-center justify-between gap-2">
                                    <div
                                        onClick={() => toggleAreaExpand(area.id)}
                                        className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer select-none"
                                    >
                                        <div className="w-6 h-6 rounded-[6px] bg-[#EFF6FF] dark:bg-blue-950/60 text-[#1D4ED8] dark:text-blue-300 font-mono font-bold text-[11px] flex items-center justify-center shrink-0">
                                            {areaIdx + 1}
                                        </div>
                                        <h4 className="text-[14px] font-bold text-[#0F172A] dark:text-slate-100 leading-snug line-clamp-2">
                                            {area.title}
                                        </h4>
                                    </div>

                                    <div className="flex items-center gap-1 shrink-0">
                                        {/* Menú ⋮ del área */}
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveMenuKey(prev => (prev === areaMenuKey ? null : areaMenuKey));
                                                }}
                                                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                                                aria-label="Acciones de área"
                                            >
                                                <span className="material-symbols-outlined text-[20px]">more_vert</span>
                                            </button>

                                            {isAreaMenuOpen && (
                                                <div
                                                    ref={menuRef}
                                                    className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-30 animate-in fade-in zoom-in duration-100"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => handleOpenCreateParam(area.id)}
                                                        className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px] text-blue-600">add</span>
                                                        <span>Añadir parámetro</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleOpenEditArea(area)}
                                                        className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px] text-slate-600 dark:text-slate-300">edit</span>
                                                        <span>Editar área</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setActiveMenuKey(null);
                                                            setConfirmDelete({
                                                                type: 'area',
                                                                areaId: area.id,
                                                                title: area.title
                                                            });
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#DC2626] hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700/60"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">delete</span>
                                                        <span>Eliminar área</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Chevron */}
                                        <button
                                            type="button"
                                            onClick={() => toggleAreaExpand(area.id)}
                                            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                        >
                                            <span className="material-symbols-outlined text-[20px]">
                                                {isExpanded ? 'expand_less' : 'expand_more'}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                {/* Segunda línea: pastilla 'N parámetros' y la insignia ámbar 'Dcto. máx. 10%' */}
                                <div className="flex items-center gap-2 pt-0.5">
                                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-[10px] bg-slate-100 dark:bg-slate-700 text-[#475569] dark:text-slate-300">
                                        {area.parameters.length} parámetros
                                    </span>
                                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-[10px] bg-[#FEF3C7] text-[#B45309] dark:bg-amber-950/40 dark:text-amber-300">
                                        Dcto. máx. {area.maxDiscountPercent !== undefined ? area.maxDiscountPercent : 10}%
                                    </span>
                                </div>

                                {/* Parámetros: filas de 44px (desplegadas en acordeón) */}
                                {isExpanded && (
                                    <div className="flex flex-col gap-1.5 pt-2 border-t border-slate-100 dark:border-slate-700/60 mt-1">
                                        {area.parameters.length === 0 ? (
                                            <div className="text-center py-4 text-xs text-slate-400 italic">
                                                Sin parámetros. Toca en ⋮ para añadir uno.
                                            </div>
                                        ) : (
                                            area.parameters.map((param, pIdx) => {
                                                const paramMenuKey = `param-${param.id}`;
                                                const isParamMenuOpen = activeMenuKey === paramMenuKey;

                                                return (
                                                    <div
                                                        key={param.id}
                                                        className="min-h-[44px] flex items-center gap-2 px-3 py-1.5 bg-[#F8FAFC] dark:bg-slate-900/60 rounded-[10px] border border-[#F1F5F9] dark:border-slate-800 relative"
                                                    >
                                                        <span className="material-symbols-outlined text-slate-400 text-[18px] shrink-0">
                                                            check_circle_outline
                                                        </span>
                                                        <span className="font-mono text-[11px] font-semibold text-[#64748B] dark:text-slate-400 shrink-0">
                                                            {areaIdx + 1}.{pIdx + 1}
                                                        </span>
                                                        <span className="text-[13px] font-medium text-[#0F172A] dark:text-slate-100 leading-snug line-clamp-2 flex-1 min-w-0">
                                                            {param.name}
                                                        </span>

                                                        {/* Menú ⋮ del parámetro */}
                                                        <div className="relative shrink-0">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActiveMenuKey(prev => (prev === paramMenuKey ? null : paramMenuKey));
                                                                }}
                                                                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                                                aria-label="Acciones de parámetro"
                                                            >
                                                                <span className="material-symbols-outlined text-[18px]">more_vert</span>
                                                            </button>

                                                            {isParamMenuOpen && (
                                                                <div
                                                                    ref={menuRef}
                                                                    className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-30 animate-in fade-in zoom-in duration-100"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleOpenEditParam(area.id, param)}
                                                                        className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[16px] text-slate-600 dark:text-slate-300">edit</span>
                                                                        <span>Editar</span>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setActiveMenuKey(null);
                                                                            setConfirmDelete({
                                                                                type: 'param',
                                                                                areaId: area.id,
                                                                                paramId: param.id,
                                                                                title: param.name
                                                                            });
                                                                        }}
                                                                        className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#DC2626] hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700/60"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[16px]">delete</span>
                                                                        <span>Eliminar</span>
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Diálogo Editar/Crear Área */}
            <KlDialog
                open={areaDialogOpen}
                onClose={() => setAreaDialogOpen(false)}
                title={editingArea?.id ? 'Editar Área de Evaluación' : 'Nueva Área de Evaluación'}
                confirmText="Guardar"
                cancelText="Cancelar"
                onConfirm={handleSaveArea}
            >
                <div className="flex flex-col gap-3 py-2">
                    <div>
                        <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                            Nombre del Área
                        </label>
                        <input
                            type="text"
                            value={editingArea?.title || ''}
                            onChange={(e) => setEditingArea(prev => prev ? { ...prev, title: e.target.value } : null)}
                            placeholder="Ej: Prevención de Riesgos"
                            className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                            Descuento Máximo (%)
                        </label>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            value={editingArea?.maxDiscountPercent ?? 10}
                            onChange={(e) => setEditingArea(prev => prev ? { ...prev, maxDiscountPercent: parseFloat(e.target.value) || 0 } : null)}
                            className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                        />
                    </div>
                </div>
            </KlDialog>

            {/* Diálogo Editar/Crear Parámetro */}
            <KlDialog
                open={paramDialogOpen}
                onClose={() => setParamDialogOpen(false)}
                title={editingParam?.paramId ? 'Editar Parámetro' : 'Nuevo Parámetro'}
                confirmText="Guardar"
                cancelText="Cancelar"
                onConfirm={handleSaveParam}
            >
                <div className="flex flex-col gap-2 py-2">
                    <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300">
                        Nombre del Parámetro
                    </label>
                    <textarea
                        rows={3}
                        value={editingParam?.name || ''}
                        onChange={(e) => setEditingParam(prev => prev ? { ...prev, name: e.target.value } : null)}
                        placeholder="Ej: Libro de obras firmado y al día"
                        className="w-full p-2.5 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB] resize-none"
                        autoFocus
                    />
                </div>
            </KlDialog>

            {/* Diálogo de Confirmación para Eliminar */}
            <KlDialog
                open={confirmDelete !== null}
                onClose={() => setConfirmDelete(null)}
                title={confirmDelete?.type === 'area' ? '¿Eliminar área?' : '¿Eliminar parámetro?'}
                message={`¿Confirmas que deseas eliminar "${confirmDelete?.title}"? Esta acción no se puede deshacer.`}
                confirmText="Eliminar"
                cancelText="Cancelar"
                danger={true}
                onConfirm={() => {
                    if (confirmDelete?.type === 'area') handleDeleteAreaConfirm();
                    else handleDeleteParamConfirm();
                }}
            />
        </KlFullModal>
    );
};
